import { consola } from 'consola'
import { ref } from 'vue'
import { Capacitor, registerPlugin } from '@capacitor/core'
const RustoreUpdate = registerPlugin<any>('RustoreUpdate')

declare global {
  interface Window {
    rustoreappupdate?: {
      init?: () => void
      get_appupdateinfo?: () => void
      register_listener?: () => void
      start_update_flow_silent?: () => void
      complete_update_flexible?: () => void
      complete_update_silent?: () => void
    }
    rustorecore?: {
      connect?: (
        eventName: string,
        handler: (self: unknown, channel: string, value: string) => void,
      ) => void
      disconnect?: (eventName: string) => void
    }
  }
}

/**
 * Composable для интеграции с RuStore In‑app Updates (Defold SDK)
 * Безопасно вызывает native‑мост, если он доступен.
 */
export const useRustoreUpdate = () => {
  const isInitialized = ref(false)
  const isBridgeAvailable = ref(false)

  const logPrefix = '[RUSTORE-UPDATE]'
  const log = (...args: any[]) => consola.debug(logPrefix, ...args)
  const warn = (...args: any[]) => consola.warn(logPrefix, ...args)
  const error = (...args: any[]) => consola.error(logPrefix, ...args)

  const detectBridge = (): boolean => {
    if (!process.client) return false
    if (!Capacitor.isNativePlatform()) return false
    const has = typeof window !== 'undefined' && !!window.rustoreappupdate
    isBridgeAvailable.value = has
    return has
  }

  /**
   * Аналог Lua-функции `init(self) rustoreappupdate.init() end`
   * Инициализирует RuStore SDK через доступный мост.
   */
  const init = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) {
        warn('Not a native platform. Skipping init.')
        return false
      }

      // Prefer Capacitor plugin if available
      if (Capacitor.isPluginAvailable('RustoreUpdate')) {
        log("[RuStore] ---1---")
        await RustoreUpdate.init()
        isInitialized.value = true
        isBridgeAvailable.value = true
        log('Initialized via Capacitor plugin')
        return true
      }else {
        log("[RuStore] ---0---")
      }

      // Fallback: Defold bridge (if present)
      if (!detectBridge()) {
        warn('Native bridge not available. Skipping init.')
        return false
      }
      const api = window.rustoreappupdate
      api?.init?.()
      isInitialized.value = true
      log('Initialized via Defold bridge')
      return true
    } catch (error_: any) {
      error('Init failed:', error_?.message || error_)
      return false
    }
  }

  const getAppUpdateInfo = async (): Promise<{ available: boolean } | null> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return null
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return null
      const res = await (RustoreUpdate as any).getAppUpdateInfo()
      return { available: !!res?.available }
    } catch (error_: any) {
      warn('getAppUpdateInfo failed:', error_?.message || error_)
      return null
    }
  }

  const startUpdateImmediate = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return false
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return false
      await (RustoreUpdate as any).startUpdateFlowImmediate()
      return true
    } catch (error_: any) {
      warn('startUpdateImmediate failed:', error_?.message || error_)
      return false
    }
  }

  const startUpdateFlexible = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return false
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return false
      await (RustoreUpdate as any).startUpdateFlowFlexible()
      return true
    } catch (error_: any) {
      warn('startUpdateFlexible failed:', error_?.message || error_)
      return false
    }
  }

  const startUpdateSilent = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return false
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return false
      await (RustoreUpdate as any).startUpdateFlowSilent()
      return true
    } catch (error_: any) {
      warn('startUpdateSilent failed:', error_?.message || error_)
      return false
    }
  }

  const completeUpdateFlexible = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return false
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return false
      await (RustoreUpdate as any).completeUpdateFlexible()
      return true
    } catch (error_: any) {
      warn('completeUpdateFlexible failed:', error_?.message || error_)
      return false
    }
  }

  const completeUpdateSilent = async (): Promise<boolean> => {
    try {
      if (!process.client || !Capacitor.isNativePlatform()) return false
      if (!Capacitor.isPluginAvailable('RustoreUpdate')) return false
      await (RustoreUpdate as any).completeUpdateSilent()
      return true
    } catch (error_: any) {
      warn('completeUpdateSilent failed:', error_?.message || error_)
      return false
    }
  }

  /**
   * Опциональная подписка на события, если мост поддерживает `rustorecore.connect`.
   * Рекомендуется вызывать ДО обращения к методам получения информации об обновлении.
   */
  const subscribeBasicEvents = (
    onGetInfoSuccess?: (payload: any) => void,
    onGetInfoFailure?: (payload: any) => void,
  ): void => {
    if (!process.client || !Capacitor.isNativePlatform()) return
    const core = window.rustorecore
    if (typeof core?.connect !== 'function') {
      warn('`rustorecore.connect` is not available; events will not be received')
      return
    }

    try {
      core.connect('rustore_get_app_update_info_success', (_self, _channel, value) => {
        log('get_app_update_info_success:', value)
        try {
          const data = safeParseJSON(value)
          onGetInfoSuccess?.(data)
        } catch {
          onGetInfoSuccess?.(value)
        }
      })

      core.connect('rustore_get_app_update_info_failure', (_self, _channel, value) => {
        warn('get_app_update_info_failure:', value)
        try {
          const data = safeParseJSON(value)
          onGetInfoFailure?.(data)
        } catch {
          onGetInfoFailure?.(value)
        }
      })
    } catch (error_: any) {
      warn('Failed to subscribe to RuStore events:', error_?.message || error_)
    }
  }

  const safeParseJSON = (value: any): any => {
    if (typeof value !== 'string') return value
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  return {
    // state
    isInitialized,
    isBridgeAvailable,

    // actions
    init,
    getAppUpdateInfo,
    startUpdateImmediate,
    startUpdateFlexible,
    startUpdateSilent,
    completeUpdateFlexible,
    completeUpdateSilent,
    subscribeBasicEvents,

    // helpers
    detectBridge,
  }
}

export default useRustoreUpdate


